import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface StrengthData {
  rank: number;
  name: string;
  domain: string;
  score?: number;
}

interface PDFExportProps {
  userName: string;
  strengths: StrengthData[];
  topDomains?: string[];
  completionDate?: string;
}

export default function PDFExport({
  userName,
  strengths,
  topDomains = [],
  completionDate,
}: PDFExportProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();

  const generatePDFContent = (): string => {
    // Generate HTML content for PDF
    const currentDate = completionDate || new Date().toLocaleDateString();
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>CliftonStrengths Report - ${userName}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #9b87f5;
            padding-bottom: 20px;
          }
          .header h1 {
            color: #9b87f5;
            margin-bottom: 10px;
          }
          .header .date {
            color: #666;
            font-size: 14px;
          }
          .section {
            margin-bottom: 40px;
          }
          .section h2 {
            color: #9b87f5;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .strength-item {
            display: flex;
            align-items: center;
            padding: 15px;
            margin-bottom: 10px;
            background: #f9fafb;
            border-radius: 8px;
            border-left: 4px solid #9b87f5;
          }
          .strength-rank {
            font-size: 24px;
            font-weight: bold;
            color: #9b87f5;
            margin-right: 20px;
            min-width: 50px;
          }
          .strength-info {
            flex: 1;
          }
          .strength-name {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 5px;
          }
          .strength-domain {
            font-size: 14px;
            color: #666;
          }
          .domain-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            margin-right: 8px;
          }
          .domain-strategic { background: #dbeafe; color: #1e40af; }
          .domain-executing { background: #f3e8ff; color: #6b21a8; }
          .domain-influencing { background: #fed7aa; color: #c2410c; }
          .domain-relationship { background: #d1fae5; color: #065f46; }
          .top-domains {
            display: flex;
            gap: 10px;
            margin-top: 10px;
          }
          .footer {
            margin-top: 60px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CliftonStrengths Profile</h1>
          <h2>${userName}</h2>
          <p class="date">Generated on ${currentDate}</p>
        </div>

        ${topDomains.length > 0 ? `
        <div class="section">
          <h2>Top Domains</h2>
          <div class="top-domains">
            ${topDomains.map(domain => {
              const domainClass = domain.toLowerCase().replace(' ', '-');
              return `<span class="domain-badge domain-${domainClass}">${domain}</span>`;
            }).join('')}
          </div>
        </div>
        ` : ''}

        <div class="section">
          <h2>Your 34 CliftonStrengths (Ranked)</h2>
          ${strengths.map((strength, index) => {
            const domainClass = strength.domain.toLowerCase().replace(' ', '-');
            return `
              <div class="strength-item">
                <div class="strength-rank">#${strength.rank}</div>
                <div class="strength-info">
                  <div class="strength-name">${strength.name}</div>
                  <div class="strength-domain">
                    <span class="domain-badge domain-${domainClass}">${strength.domain}</span>
                    ${strength.score ? `<span>${strength.score}%</span>` : ''}
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <div class="footer">
          <p>This report was generated by the CliftonStrengths Assessment Platform</p>
          <p>© ${new Date().getFullYear()} - All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;

    return html;
  };

  const handleExportPDF = () => {
    try {
      // In a real implementation, you would use a library like jsPDF or html2pdf
      // For now, we'll create a downloadable HTML file
      const htmlContent = generatePDFContent();
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CliftonStrengths_Report_${userName.replace(/\s/g, '_')}_${Date.now()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: t('export.success'),
        description: t('export.successDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('export.error'),
        variant: "destructive",
      });
    }
  };

  const handleExportJSON = () => {
    try {
      const jsonData = {
        userName,
        strengths,
        topDomains,
        exportDate: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CliftonStrengths_Data_${userName.replace(/\s/g, '_')}_${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: t('export.success'),
        description: t('export.jsonSuccessDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('export.error'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap" dir={isRtl ? "rtl" : "ltr"}>
      <Button onClick={handleExportPDF} className="w-full gap-2 sm:w-auto">
        <Download className="w-4 h-4" />
        {t('export.downloadReport')}
      </Button>
      <Button onClick={handleExportJSON} variant="outline" className="w-full gap-2 sm:w-auto">
        <FileText className="w-4 h-4" />
        {t('export.downloadJSON')}
      </Button>
    </div>
  );
}
